import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Timeline } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

/** One labelled line inside a job row. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="dp-muted">{label}</span> {children}
    </div>
  );
}

interface Job {
  name: string;
  when: string;
  does: string;
  see: string;
  change: string;
}

const BILLING: Job[] = [
  {
    name: 'Overdue invoice detection',
    when: 'Daily at 01:00, every active workspace.',
    does: 'A pending invoice whose due date has passed becomes Overdue. Credit notes are never aged. Only invoices still pending at the moment of the write are changed, so a payment that lands during the run wins.',
    see: 'The invoice status changes to Overdue and an in-app overdue-invoice notification is raised for it.',
    change: 'No setting. The due date comes from the invoice payment terms.',
  },
  {
    name: 'Payment reminders',
    when: 'Daily at 09:00, every active workspace whose Automatically send emails setting is not off.',
    does: 'Sends reminder emails at six stages: 7 days before the due date, on the due date, and 1, 7, 14 and 30 days after it.',
    see: 'The reminder emails your clients receive, using the wording on the Templates page.',
    change:
      'Settings, General, Email Settings, Automatically send emails (default on). Turning it off pauses all scheduled emails of this kind. Edit the wording under Templates.',
  },
  {
    name: 'Churn reconciliation',
    when: 'Daily at 03:00, every workspace except Support sandboxes.',
    does: 'A subscription whose end date passed more than the churn grace period ago, with no renewal, becomes a churn event in the lifecycle log. If the subscription had a scheduled cancellation, the reason is customer request; otherwise natural expiration. It skips subscriptions that have a renewal event for the same client inside the grace window, subscriptions that never went live, and any whose final invoice is still held as a draft.',
    see: 'A Churned event in the lifecycle log, which books the lost MRR in the MRR movement chart and in churn and NRR. Before it books the churn, a cancelled subscription that still owes a closing invoice has one raised.',
    change:
      'Settings, General, Analytics Settings, Churn Grace Period. Default 30 days. The reactivation window must be at least as long.',
  },
  {
    name: 'Subscription start, renewal, expiry and grace',
    when: 'Daily at 06:00, every active workspace. The billing-schedule sweep runs an hour earlier, at 05:00.',
    does: 'In order: subscriptions whose start date has arrived move from Pending to active; contract renewal notices are raised for committed terms inside their notice window; every subscription whose term has ended is processed. With auto-renew on, it renews and invoices the next period. With auto-renew off, any usage still owed is invoiced, then the subscription enters grace if it has grace days, or expires at once. A subscription in grace expires when its grace end date passes.',
    see: 'Subscription status moves between Active, Grace period and Expired. Renewal invoices appear on Finance. Overdue renewals and subscriptions awaiting payment show on the current month panel.',
    change:
      'Auto-renew and grace days are set per subscription. There is no workspace switch for the renewal run; the old Auto renewal workspace feature gates nothing.',
  },
  {
    name: 'Advance renewal invoicing',
    when: 'Part of the 06:00 renewal run.',
    does: 'Generates the renewal invoice ahead of the renewal date once the lead window is open, dated that day, for the upcoming period. Only the recurring base charge is billed ahead. Usage billed in arrears is billed at renewal. A subscription with no base charge is never invoiced ahead.',
    see: 'The renewal invoice exists before the renewal date.',
    change:
      'Settings, General, Renewal Invoice Timing: lead days for monthly, quarterly and yearly subscriptions. Default 0, which invoices on the renewal date. A client can override each period on its edit screen; 0 on the client switches it off.',
  },
  {
    name: 'Owed invoice retry',
    when: 'Part of the 06:00 renewal run, after advance invoicing; also hourly for held usage drafts (see Usage metering).',
    does: 'Retries a renewal invoice that a previous run failed to raise, and any billing-schedule charge still owed, such as a prepaid span whose start invoice failed. A debt is cleared only after the work it stands for has completed, so a retry that throws leaves it owed for the next run.',
    see: 'The missing invoice appears once a retry succeeds. Nothing is shown while it is still failing.',
    change: 'No setting.',
  },
  {
    name: 'Billing-schedule sweep',
    when: 'Daily at 05:00, every active workspace.',
    does: 'Promotes subscriptions whose start date has arrived and raises the activation-overdue alert for deferred-activation subscriptions that are still waiting.',
    see: 'Pending subscriptions become active; overdue activations are flagged.',
    change: 'No setting. Only meaningful where Billing schedules is granted.',
  },
  {
    name: 'Custom pricing agreement expiry',
    when: 'Daily at 02:00, all workspaces.',
    does: 'Moves agreements whose end date has passed to expired, so the price walk stops using them.',
    see: 'The agreement shows as expired and the catalog price applies again.',
    change: 'Set the agreement end date.',
  },
  {
    name: 'Exchange rates',
    when: 'Daily at 02:00.',
    does: 'Fetches that day’s global rates (one EUR-based set shared by all workspaces) and fills yesterday’s if it is missing. A past date with no stored rate is fetched on demand; a rate is never approximated from another day.',
    see: 'Converted figures in analytics. If no authoritative rate can be obtained the calculation errors rather than showing a blended figure.',
    change: 'No workspace setting. Set the reporting currency under Settings, General.',
  },
  {
    name: 'Analytics snapshots',
    when: 'Daily at 02:00 for yesterday; monthly at 03:00 on the 1st for the month just ended.',
    does: 'Writes the stored daily and monthly snapshot of MRR, ARR, churn, expansion and NRR for every active workspace. Separate from this, any new lifecycle event or discount change queues a recalculation of snapshots from the affected date to today, after a 1.5 second wait that merges bursts.',
    see: 'History on the dashboard. A backdated change rewrites the days it touches.',
    change: 'No setting.',
  },
];

const USAGE: Job[] = [
  {
    name: 'Usage period close',
    when: 'Daily at 04:00, every workspace with metered usage.',
    does: 'Closes every usage accumulator whose period has ended, at the boundary the period actually ended on, and writes the period reading. It runs before the 06:00 renewal so arrears usage is already closed when the invoice is built. A period cannot be closed twice.',
    see: 'A reading for the closed period, awaiting its seal.',
    change: 'No setting.',
  },
  {
    name: 'Usage seal and late arrivals',
    when: 'Hourly. A closed period waits 24 hours before it is sealed.',
    does: 'After the wait, a reading is sealed and becomes the billable number. For a metered API period that received nothing, the reading is marked unverified instead and is not billed as zero. A manual-entry period stays awaiting for an operator. The invoice for a period is held as a draft until its readings release; each hourly run also retries drafts whose release was interrupted.',
    see: 'Held drafts on Finance, awaiting-entry and unverified counts on the Metered usage action items card on the dashboard.',
    change:
      'The 24 hour wait is set by Revvly deployment configuration. Enter or waive a period to release its invoice.',
  },
  {
    name: 'Late-arrival flag',
    when: 'On each usage event received.',
    does: 'An event reported more than 24 hours before it arrived is stored with a late-arrival flag. The flag is separate from the seal wait and does not reject the event.',
    see: 'The flag on the event in the usage audit.',
    change: 'No setting.',
  },
];

const SUPPORT: Job[] = [
  {
    name: 'Auto-close',
    when: 'Every 5 minutes.',
    does: 'Closes a ticket that has waited on the client, in a waiting status such as Resolved or Answered, longer than the auto-close time with no client reply. Up to 500 tickets per run across all workspaces.',
    see: 'The ticket closes.',
    change:
      'Settings, Support, General, Auto-close. Default 48 hours, from 1 to 720. Turn the switch off to disable it.',
  },
  {
    name: 'Assumed AI resolution',
    when: 'Every 5 minutes, looking back 14 days.',
    does: 'A ticket the AI answered, where no person replied in public and the customer has been silent for 24 hours after the last AI message, is counted as an AI resolution. Practice answers and answers the customer already judged are not counted.',
    see: 'The ticket counts toward the month’s billable AI resolutions.',
    change:
      'The 24 hour window is fixed. Whether the AI answers at all is Settings, Support, General, AI answers first.',
  },
  {
    name: 'Client reply emails',
    when: 'Checked every minute.',
    does: 'Emails the client about a public reply that stays unread for the client email delay. Up to three attempts. An email still owed 24 hours after it fell due is dropped.',
    see: 'The email the client receives.',
    change: 'Settings, Support, General, minutes unread. Default 5, from 1 to 1,440.',
  },
  {
    name: 'Agent notification emails',
    when: 'Every 15 seconds.',
    does: 'Sends the email half of agent notifications: new ticket, assigned to you, new reply, status changed, mentioned. The email carries the ticket number, title and a link, not the message text. Up to three attempts.',
    see: 'The email to the agent.',
    change: 'No workspace setting.',
  },
  {
    name: 'Client progress updates',
    when: 'Checked every minute.',
    does: 'Posts a progress update to the client when a ticket moves to a new client-facing stage. Moves closer than 10 minutes apart make one update about the latest stage. A failed post is retried after 5 minutes.',
    see: 'An update message on the ticket thread.',
    change: 'Settings, Support, Client updates.',
  },
  {
    name: 'Ticket titles and summaries',
    when: 'Checked every minute.',
    does: 'Gives an untitled ticket a short title, and writes a summary of a closed ticket, for tickets closed in the last 7 days. Both obey the AI on switch and the daily spend cap, and are never billable.',
    see: 'The title and the summary on the ticket.',
    change: 'Settings, Support, General, AI switches and the daily spend cap.',
  },
  {
    name: 'Knowledge refresh',
    when: 'Checked every 5 minutes.',
    does: 'Re-reads a website or help-centre source whose schedule is daily or weekly and which is due. A failing source backs off 5 minutes, then 10, doubling up to 6 hours. A site is read only after its owner has confirmed ownership.',
    see: 'Updated articles in Knowledge, and a failed-source count when reading fails.',
    change:
      'The schedule on each knowledge source: manual, daily or weekly. New sources are manual.',
  },
  {
    name: 'Attachment cleanup',
    when: 'Hourly.',
    does: 'Deletes support attachments that were uploaded but never sent on a message, once they are older than 24 hours. Up to 500 per run.',
    see: 'Nothing. A draft left open over lunch still sends.',
    change: 'No setting.',
  },
  {
    name: 'Support webhooks',
    when: 'Every 5 seconds.',
    does: 'Delivers ticket events to your endpoints. Retries after 10 seconds, 1 minute, 5 minutes, 30 minutes, 2 hours and 6 hours. A delivery still owed after 24 hours is dropped. After 10 deliveries in a row give up, the endpoint switches itself off. Up to 5 endpoints.',
    see: 'Delivery status in Settings, Support, Webhooks.',
    change: 'Enable or disable each endpoint there.',
  },
];

const PEOPLE: Job[] = [
  {
    name: 'OKR weekly prompt',
    when: 'Checked hourly at :15. Sent at each person’s own local slot. Default Monday at 09:00.',
    does: 'Sends each person the list of check-ins due. Nobody with nothing due is messaged, and nothing is sent unless the cycle is active.',
    see: 'A message in Slack, or in ClickUp Chat if the workspace has switched OKR messages to it.',
    change:
      'Settings, Notifications, Check-in rhythm: the workspace default day and time. Each person can set their own in their profile notification preferences. The OKR module must be turned on under Settings, Modules.',
  },
  {
    name: 'OKR daily reminder',
    when: 'Checked hourly at :45. Sent at the same local hour as the prompt, on weekdays other than the prompt day.',
    does: 'Chases only people the weekly prompt was delivered to in the last week, and only while a key result that prompt listed is still unanswered. It stops by itself when the check-ins are done or the next weekly prompt takes over.',
    see: 'A still-due message.',
    change: 'Same settings as the weekly prompt.',
  },
  {
    name: 'OKR scoring reminder',
    when: 'Checked hourly at :35. Weekly, at each person’s local slot, starting 3 days after scoring opens.',
    does: 'Nudges owners who still owe scores while a cycle is being scored.',
    see: 'A message addressed to the person who owes the score.',
    change: 'Same settings as the weekly prompt.',
  },
  {
    name: 'OKR digest',
    when: 'Checked hourly at :25. Once per cycle per week per reader, at their local slot.',
    does: 'Sends cycle owners the stale, declining, unowned, unattached and metric-stalled items, plus how many people were prompted. Team leads receive their team’s part when enabled. A digest with nothing to report is not sent.',
    see: 'A digest message.',
    change:
      'Settings, Notifications, Check-in rhythm: day, time, and Copy team leads on the weekly digest.',
  },
  {
    name: 'OKR metric refresh',
    when: 'Daily at 03:30, after the 02:00 snapshot.',
    does: 'Reads each metric-backed key result’s metric and writes the value back. On failure the last value is kept and a failure counter rises. Three failures in a row mark the data stale.',
    see: 'Key result values, and a stale-data indicator.',
    change: 'No setting.',
  },
  {
    name: 'ClickUp work-link refresh',
    when: 'At :00 and :30 every hour.',
    does: 'Refreshes the status of ClickUp tasks linked to key results in cycles that are not closed. A link tried in the last 25 minutes is skipped; up to 200 links per workspace per pass.',
    see: 'Current status on linked tasks.',
    change:
      'Connect or disconnect ClickUp under Settings, Integrations. The sweep runs only for workspaces with ClickUp connected.',
  },
  {
    name: 'ClickUp Chat polling',
    when: 'Every minute, per workspace with ClickUp connected and Chat on.',
    does: 'ClickUp has no chat webhook, so replies are polled. A person in a conversation, or messaged in the last 15 minutes, is read every minute. A chat messaged within the last day is read about every fifth minute. Older chats are not read. Up to 60 chats per pass.',
    see: 'A reply typed in ClickUp Chat is picked up within about a minute while a conversation is live.',
    change: 'Settings, Integrations, ClickUp, Chat.',
  },
  {
    name: 'Digest folding',
    when: 'Hourly at :05, at each person’s local hour.',
    does: 'Folds a person’s queued digest-type messages into one message.',
    see: 'One message instead of several.',
    change: 'Settings, Notifications and each person\u2019s profile notification preferences.',
  },
  {
    name: 'Slack invitations',
    when: 'Expiry daily at 03:20. Reminder daily at 09:40.',
    does: 'An invitation expires 14 days after it is sent. One reminder goes on day 7 to a pending invitation, with a rotated link. A workspace with no Slack connection is skipped and its reminder is not spent.',
    see: 'The invitation status moves to Expired; the reminder DM.',
    change: 'Resend an invitation to start a new 14 days.',
  },
  {
    name: 'Recurring tasks',
    when: 'Every 30 minutes.',
    does: 'Creates the next task of any active recurring series that is due, whether or not the previous one was completed. The schedule is advanced before the task is created, so concurrent workers cannot create it twice.',
    see: 'A new task on its due schedule.',
    change: 'Deactivate the series. Revvly can switch the sweep off for a deployment.',
  },
  {
    name: 'Task reminders',
    when: 'Every minute.',
    does: 'Finds tasks whose reminder time has passed and are still open, and marks the reminder sent. The only visible effect today is on usage tasks: a manual-usage or unverified-usage task rises to Urgent. Notifications are not attached yet.',
    see: 'Urgent priority on a usage task.',
    change: 'Revvly can switch the sweep off for a deployment.',
  },
  {
    name: 'Account situations',
    when: 'Daily at 06:00, every workspace.',
    does: 'Re-evaluates each account’s situation from its email threads and facts, such as a stalled account.',
    see: 'Entries on the Situations page.',
    change: 'Thresholds under Settings, Situations.',
  },
  {
    name: 'Email AI passes',
    when: 'Tier 1 hourly on the hour. Tier 2 hourly at :30.',
    does: 'Tier 1 classifies escalated inbound email. Tier 2 extracts commitments from messages Tier 1 qualified. Each pass runs only for workspaces holding the matching feature, and only while Revvly has configured the model and a positive monthly budget.',
    see: 'Triage results and overdue-commitment situations.',
    change: 'Granted by Revvly; see Workspace features.',
  },
  {
    name: 'Slack connection health',
    when: 'Every 15 minutes, at :07, :22, :37 and :52.',
    does: 'Checks that the Slack connection still works, because a token revoked in Slack’s admin console announces nothing.',
    see: 'A degraded-connection banner when the check fails.',
    change: 'Reconnect Slack under Settings, Integrations.',
  },
];

const HOUSEKEEPING: Job[] = [
  {
    name: 'Document link expiry',
    when: 'Not a scheduled job. Checked each time a link is opened.',
    does: 'A shared document link works until its expiry date or until it is revoked. After that it answers with the same page as any dead link. Default expiry 180 days, at most 730.',
    see: 'The unavailable page for the recipient.',
    change: 'Set the expiry when you create the link, or revoke the link.',
  },
  {
    name: 'Library upload cleanup',
    when: 'Hourly.',
    does: 'Deletes library uploads that never became a document version, once older than 24 hours. A failed deletion is retried after a day.',
    see: 'Nothing.',
    change: 'No setting.',
  },
  {
    name: 'Form upload cleanup',
    when: 'Hourly.',
    does: 'Deletes files uploaded to a form that no submission claimed, once older than 24 hours.',
    see: 'Nothing.',
    change: 'No setting.',
  },
  {
    name: 'Assistant attachment cleanup',
    when: 'Daily at 07:00, every workspace.',
    does: 'Deletes chat attachments that no conversation references, once older than 24 hours. An attachment a conversation uses is never deleted.',
    see: 'Nothing.',
    change: 'No setting.',
  },
  {
    name: 'Form action steps',
    when: 'Every 15 seconds.',
    does: 'Runs the pending follow-up steps of form submissions. A step is claimed with a lease and renewed before each step. A workspace that has lost Forms has its steps held, not run.',
    see: 'Step results on the submission.',
    change: 'Edit the form follow-up steps, or retry failed steps.',
  },
  {
    name: 'Outbound webhooks',
    when: 'Every 5 seconds.',
    does: 'Delivers workspace webhook events, at most 25 per endpoint per pass. Up to 7 attempts, waiting 30 seconds, 2 minutes, 10 minutes, 1 hour, 6 hours, then 12 hours. After 25 final failures in a row the endpoint switches itself off, and its queued deliveries are given up after 7 days.',
    see: 'Delivery history for the endpoint.',
    change: 'Settings, Webhooks: enable or disable each endpoint.',
  },
];

const DAILY: [string, string][] = [
  ['01:00', 'Overdue invoice detection'],
  ['02:00', 'Exchange rates, daily snapshots, custom pricing agreement expiry'],
  ['03:00', 'Churn reconciliation; monthly snapshots on the 1st'],
  ['03:20', 'Slack invitation expiry'],
  ['03:30', 'OKR metric refresh'],
  ['04:00', 'Usage period close'],
  ['05:00', 'Billing-schedule sweep'],
  [
    '06:00',
    'Start dates, renewals, expiry and grace, advance invoicing, owed retries; account situations',
  ],
  ['07:00', 'Assistant attachment cleanup'],
  ['09:00', 'Payment reminders'],
  ['09:40', 'Slack invitation reminder'],
];

function JobTable({ jobs }: { jobs: Job[] }) {
  return (
    <div className="dp-fields-table">
      {jobs.map((j) => (
        <div className="dp-field-row" key={j.name}>
          <div className="dp-field-k">{j.name}</div>
          <div className="dp-field-v">
            <Fact label="When">{j.when}</Fact>
            <Fact label="What happens">{j.does}</Fact>
            <Fact label="You see">{j.see}</Fact>
            <Fact label="Change or switch off">{j.change}</Fact>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AutomaticJobsReference() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'What runs automatically — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Automatic jobs</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          What runs automatically, <em>and when</em>.
        </h1>
        <p className="dp-oneliner">
          Every scheduled job and sweep whose effect you can observe: what it does, when it runs,
          what you see, and how to change it or turn it off.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          How to read this page
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Clock</div>
            <div className="dp-field-v">
              Clock times are the server clock. No schedule in the code sets a time zone, and code
              comments describe the daily ones as UTC, so read them as UTC unless Revvly tells you
              the servers run in another zone. Jobs marked local slot use each person&rsquo;s own
              time zone, or the workspace time zone when the person has none.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Which workspaces</div>
            <div className="dp-field-v">
              Daily billing jobs run for every active workspace. They do not depend on a workspace
              feature; the one that used to, Auto renewal, no longer gates anything.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Several servers</div>
            <div className="dp-field-v">
              Sweeps that call outside services or spend AI budget take a shared lease, so when
              several replicas run, only one of them runs a pass.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Run order</div>
            <div className="dp-field-v">
              The daily times are staggered on purpose: usage periods close before renewals bill
              them, and exchange rates and the daily snapshot are written before churn is booked. Section 07
              lists the day in order.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Not listed</div>
            <div className="dp-field-v">
              Gmail mailbox sync is handled by a separate email integration service, so its cadence
              is not set in the Revvly API code and is not listed. Internal housekeeping with no
              customer-visible effect is omitted.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Billing and revenue
        </h2>
        <JobTable jobs={BILLING} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Usage metering
        </h2>
        <JobTable jobs={USAGE} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Support
        </h2>
        <p className="dp-section-sub">
          These run only for workspaces that hold the Support feature.
        </p>
        <UiPath
          steps={['Settings', 'Support', 'General']}
          note="Auto-close, AI answering, the daily AI spend cap and the client email delay are on this screen."
        />
        <JobTable jobs={SUPPORT} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          OKRs, notifications and tasks
        </h2>
        <JobTable jobs={PEOPLE} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Documents, forms and delivery
        </h2>
        <JobTable jobs={HOUSEKEEPING} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          The day in order
        </h2>
        <div className="dp-fields-table">
          {DAILY.map(([time, what]) => (
            <div className="dp-field-row" key={time}>
              <div className="dp-field-k">{time}</div>
              <div className="dp-field-v">{what}</div>
            </div>
          ))}
        </div>
        <Figure
          title="The billing day, in order"
          caption="Usage periods close at 04:00 so renewals at 06:00 bill them, and rates and the daily snapshot land before churn is booked."
        >
          <Timeline
            items={[
              { when: '01:00', title: 'Overdue detection', text: 'Pending invoices past due become Overdue.' },
              {
                when: '02:00',
                title: 'Rates and daily snapshots',
                text: 'Exchange rates, yesterday’s snapshots, agreement expiry.',
              },
              { when: '03:00', title: 'Churn reconciliation', text: 'Ended subscriptions become churn events.' },
              { when: '04:00', title: 'Usage period close', text: 'Ended usage periods are closed.' },
              { when: '05:00', title: 'Billing-schedule sweep', text: 'Start dates arrive, overdue activations flagged.' },
              {
                when: '06:00',
                title: 'Renewals',
                text: 'Start, renew, expire, advance invoicing, owed retries.',
                tone: 'accent',
              },
              { when: '09:00', title: 'Payment reminders', text: 'Reminder emails go out.' },
            ]}
          />
        </Figure>
        <p className="dp-section-sub">
          Hourly and faster jobs are listed in their own sections. For how renewals work in full,
          see <Link to="/docs/guides/renewals">Renewals</Link>; for how usage periods bill, see{' '}
          <Link to="/docs/guides/metered-usage">Metered usage</Link>.
        </p>
      </section>
    </div>
  );
}
